/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Language Target Injection & Transcreation Directive Manager
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { TargetLanguage } from './types';

/**
 * 25 Major international languages supported by Sophia Platform.
 */
export const SUPPORTED_LANGUAGES: TargetLanguage[] = [
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', direction: 'ltr' },
  { code: 'en', name: 'English', nativeName: 'English', direction: 'ltr' },
  { code: 'es', name: 'Spanish', nativeName: 'Español', direction: 'ltr' },
  { code: 'fr', name: 'French', nativeName: 'Français', direction: 'ltr' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', direction: 'ltr' },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', direction: 'ltr' },
  { code: 'ko', name: 'Korean', nativeName: '한국어', direction: 'ltr' },
  { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '简体中文', direction: 'ltr' },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文', direction: 'ltr' },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', direction: 'ltr' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', direction: 'ltr' },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', direction: 'ltr' },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', direction: 'ltr' },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', direction: 'rtl' },
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', direction: 'ltr' },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', direction: 'ltr' },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', direction: 'ltr' },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', direction: 'ltr' },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', direction: 'ltr' },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', direction: 'ltr' },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', direction: 'ltr' },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', direction: 'ltr' },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', direction: 'ltr' },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', direction: 'ltr' },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', direction: 'ltr' },
];

/**
 * Normalization lookup map for fast language identification.
 */
const LANGUAGE_LOOKUP = new Map<string, TargetLanguage>();

for (const lang of SUPPORTED_LANGUAGES) {
  LANGUAGE_LOOKUP.set(lang.code.toLowerCase(), lang);
  LANGUAGE_LOOKUP.set(lang.name.toLowerCase(), lang);
  LANGUAGE_LOOKUP.set(lang.nativeName.toLowerCase(), lang);
}

// Add common aliases
LANGUAGE_LOOKUP.set('zh', LANGUAGE_LOOKUP.get('zh-cn')!);
LANGUAGE_LOOKUP.set('chinese', LANGUAGE_LOOKUP.get('zh-cn')!);
LANGUAGE_LOOKUP.set('vn', LANGUAGE_LOOKUP.get('vi')!);
LANGUAGE_LOOKUP.set('vietnam', LANGUAGE_LOOKUP.get('vi')!);
LANGUAGE_LOOKUP.set('tiếng việt', LANGUAGE_LOOKUP.get('vi')!);

/**
 * LanguageManager: Manages target language resolution and transcreation prompt directives.
 */
export class LanguageManager {
  /**
   * Resolve an input string (code, name, or alias) to a validated TargetLanguage.
   * Defaults to English if unspecified or unresolvable.
   */
  public static resolveLanguage(langOrCode?: string): TargetLanguage {
    if (!langOrCode || typeof langOrCode !== 'string') {
      return LANGUAGE_LOOKUP.get('en')!;
    }

    const cleaned = langOrCode.trim().toLowerCase();
    const found = LANGUAGE_LOOKUP.get(cleaned);

    if (found) {
      return found;
    }

    // Attempt prefix match (e.g. 'vi-VN' -> 'vi')
    const prefix = cleaned.split(/[-_]/)[0];
    if (prefix && LANGUAGE_LOOKUP.has(prefix)) {
      return LANGUAGE_LOOKUP.get(prefix)!;
    }

    // Default fallback
    return LANGUAGE_LOOKUP.get('en')!;
  }

  /**
   * Check whether a language identifier is recognized.
   */
  public static isSupported(langOrCode: string): boolean {
    if (!langOrCode) return false;
    const cleaned = langOrCode.trim().toLowerCase();
    const prefix = cleaned.split(/[-_]/)[0];
    return LANGUAGE_LOOKUP.has(cleaned) || Boolean(prefix && LANGUAGE_LOOKUP.has(prefix));
  }

  /**
   * Generate an uncompromising, high-priority System Prompt Directive for the target language.
   * Instructs the LLM to write in native phrasing (Transcreation style, avoiding clumsy literal translations).
   */
  public static getSystemInstruction(langOrCode?: string): string {
    const lang = this.resolveLanguage(langOrCode);

    return [
      `CRITICAL INSTRUCTION: You MUST generate all output strictly in the requested target language: ${lang.name} (${lang.nativeName}).`,
      `Maintain natural phrasing, native idioms, cultural context, and accurate local terminology.`,
      `Adopt a fluent transcreation approach rather than word-for-word machine translation.`,
      lang.direction === 'rtl' ? `Note: The target language (${lang.name}) is written in Right-To-Left (RTL) format.` : '',
    ]
      .filter(Boolean)
      .join('\n');
  }

  /**
   * Process bilingual format: Primary language text with optional English secondary text.
   */
  public static formatBilingual(primary: string, secondary?: string): string {
    const cleanPrimary = primary?.trim() || '';
    const cleanSecondary = secondary?.trim() || '';

    if (!cleanPrimary) return cleanSecondary;
    if (!cleanSecondary) return cleanPrimary;

    return `${cleanPrimary} – (${cleanSecondary})`;
  }
}
